import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  toast,
} from '@kognitos/lattice';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
} from '@/shared/stores/user-preferences.store';
import { WorkspaceIconPicker } from '@/shared/components/WorkspaceIconPicker';
import { useAuth } from '@/shared/auth';
import {
  getCurrentUserOrganizationWorkspacesQueryKey,
  getListWorkspacesQueryKey,
  useCreateWorkspace,
} from '@/generated/hooks/agora/v1/workspace';
import { useGetOrganization } from '@/generated/hooks/agora/v1/organization';
import {
  WORKSPACE_DOMAINS,
  WorkspaceColor,
  WorkspaceIcon,
  type WorkspaceIconType,
} from '@/modules/onboarding/constants';
import { captureException } from '@/modules/sentry';
import { checkIsAccessDeniedError } from '@/shared/utils/error-handler';
import { AccessDeniedAlert } from '@/shared/components';
import {
  createWorkspaceSchema,
  type CreateWorkspaceFormData,
} from '../create-workspace-schema';
import { useNavigate } from 'react-router-dom';
import { getRoutePath } from '@/routes/util';
import { parseResourceName } from '@/shared/utils';
import { useWorkspaceTypeResolver } from '@/shared/hooks/use-workspace-type-resolver';
import { isFinanceWorkspaceType } from '@/shared/utils/finance-workspace';

interface ICreateWorkspaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateWorkspaceDialog({
  isOpen,
  onClose,
}: ICreateWorkspaceDialogProps) {
  const { t } = useTranslation('workspace');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const form = useForm<CreateWorkspaceFormData>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: {
      workspaceName: '',
      workspaceIcon: WorkspaceIcon.GRIP,
      workspaceColor: WorkspaceColor.CYAN,
      workspacePurpose: '',
    },
  });
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const { data: organizationData } = useGetOrganization(currentOrganizationId, {
    query: {
      enabled: isOpen && Boolean(currentOrganizationId),
      retry: false,
    },
  });

  const {
    mutate: createWorkspace,
    isPending: isCreatingWorkspace,
    error: createWorkspaceError,
    reset: resetCreateWorkspace,
  } = useCreateWorkspace();

  const isAccessDeniedError = checkIsAccessDeniedError(createWorkspaceError);

  // Shared with the onboarding workspace step: owns the gate, so the Finance
  // pick only types the workspace when the finance experience is available.
  const resolveWorkspaceType = useWorkspaceTypeResolver();

  const onSubmit = (data: CreateWorkspaceFormData) => {
    const workspaceType = resolveWorkspaceType(data.workspacePurpose);
    const isFinanceWorkspace = isFinanceWorkspaceType(workspaceType);

    createWorkspace(
      {
        organization: currentOrganizationId,
        data: {
          name: data.workspaceName,
          title: data.workspaceName,
          workspace_icon: data.workspaceIcon,
          workspace_icon_color: data.workspaceColor,
          ...(workspaceType && { workspace_type: workspaceType }),
        },
      },
      {
        onSuccess: newWorkspace => {
          onClose();
          queryClient.invalidateQueries({
            queryKey: getCurrentUserOrganizationWorkspacesQueryKey(
              currentOrganizationId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListWorkspacesQueryKey(currentOrganizationId),
          });

          if (newWorkspace.data.name) {
            const workspaceId = parseResourceName(
              newWorkspace.data.name,
            ).workspace;
            if (workspaceId) {
              // A finance workspace opens on its cockpit rather than
              // automations — that's the experience the user just asked for. If
              // the backend didn't honour `workspace_type`, FinanceLayout
              // bounces to automations anyway.
              navigate(
                getRoutePath(
                  isFinanceWorkspace ? 'FINANCE_COCKPIT' : 'AUTOMATIONS',
                  {
                    orgId: currentOrganizationId,
                    workspaceId,
                  },
                ),
              );
            }
          }
          toast.success(t('workspace.create.success'));
        },
        onError: error => {
          captureException(error);
        },
      },
    );
  };

  // eslint-disable-next-line no-restricted-syntax -- reason: Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      form.reset();
      resetCreateWorkspace();
    }
  }, [isOpen, form, resetCreateWorkspace]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('workspace.create.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('workspace.create.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            id="create-workspace-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <Flex gap={3} align="start">
              <div className="flex flex-col">
                <FormField
                  control={form.control}
                  name="workspaceIcon"
                  render={({ field: iconField }) => (
                    <FormField
                      control={form.control}
                      name="workspaceColor"
                      render={({ field: colorField }) => (
                        <FormItem>
                          <FormControl>
                            <WorkspaceIconPicker
                              selectedIcon={
                                (iconField.value ||
                                  WorkspaceIcon.GRIP) as WorkspaceIconType
                              }
                              selectedColor={
                                colorField.value || WorkspaceColor.CYAN
                              }
                              onIconChange={iconField.onChange}
                              onColorChange={colorField.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="workspaceName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t('workspace.create.label')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('workspace.create.placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Flex>

            <FormField
              control={form.control}
              name="workspacePurpose"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-1">
                    {t('workspace.create.purpose.label')}
                    <Text color="muted" level="small">
                      {t('workspace.create.purpose.optional')}
                    </Text>
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t(
                            'workspace.create.purpose.placeholder',
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {WORKSPACE_DOMAINS.map(domain => (
                        <SelectItem key={domain} value={domain}>
                          {domain}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <Text level="small" color="muted">
                {t('workspace.create.owner')}
              </Text>
              <Text weight="semibold">{user?.name}</Text>
            </div>

            <div>
              <Text level="small" color="muted">
                {t('workspace.create.organization')}
              </Text>
              <Text weight="semibold">{organizationData?.data?.title}</Text>
            </div>
          </form>
          {createWorkspaceError &&
            (isAccessDeniedError ? (
              <AccessDeniedAlert />
            ) : (
              <Alert variant="destructive">
                <Icon type="AlertCircle" />
                <AlertDescription>
                  {t('workspace.create.error.description')}
                </AlertDescription>
              </Alert>
            ))}
        </Form>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCreatingWorkspace}
          >
            {t('workspace.create.cancel')}
          </Button>
          <Button
            type="submit"
            form="create-workspace-form"
            isLoading={isCreatingWorkspace}
            disabled={isCreatingWorkspace}
          >
            {t('workspace.create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
